;; Copyright (C) 2017, Doctor Evidence. All rights reserved.

(ns dre.shared.interface.author.core
  "Article author"
  {:authors ["Jozef Wagner"]}
  (:require [clojure.set :as set]
            [clojure.string :as str]))

;;;; Implementation details

(defn ngrams
  [s n]
  (when-not (str/blank? s)
    (let [s (str/replace s #"\W" "")
          l (count s)
          n (max 1 (or n 3))]
      (when-not (str/blank? s)
        (map #(let [from (min % (dec l))
                    len (min n (- l from))]
                (subs s from (+ from len)))
             (range 0 (max 1 (inc (- l n)))))))))

(defn jaccard-distance [a b]
  (let [count-union (count (set/union a b))
        count-intersection (count (set/intersection a b))]
    (/ (- count-union count-intersection)
       count-union)))

(defn similarity [a b]
  (- 1.0 (jaccard-distance a b)))

(defn author-affiliations
  ([affiliations]
   (author-affiliations affiliations nil))
  ([affiliations threshold]
   (let [n 3
         names (distinct (map :affiliation/name affiliations))
         affs (map #(hash-map :affiliation/name %
                              :ngrams (set (ngrams % n)))
                   names)
         affs (sort-by (comp count :affiliation/name) < affs)
         threshold (or threshold 0.5)]
     (when (seq affs)
       (loop [groups {(first affs) [(first affs)]}
              affs (rest affs)]
         (if (seq affs)
           (let [aff (first affs)
                 sf (fn [[m]]
                      (let [score (similarity (:ngrams m)
                                              (:ngrams aff))]
                        (when (<= threshold score)
                          m)))
                 winner (some sf groups)
                 groups (if winner
                          (update groups winner conj aff)
                          (assoc groups aff [aff]))]
             (recur groups (rest affs)))
           (let [winf
                 (fn [[aff affs]]
                   (let [nset (set (map :affiliation/name affs))
                         affiliations
                         (filter (comp nset :affiliation/name)
                                 affiliations)
                         years (keep (comp
                                      (fn [pub-date]
                                        (when pub-date
                                          #?(:clj (.getYear pub-date)
                                             :cljs (.getFullYear pub-date))))
                                      :article/published
                                      :article/author)
                                     affiliations)
                         n (:affiliation/name aff)]
                     (map #(vector % {:affiliation/name n
                                      :affiliation/years
                                      (sort > (distinct years))})
                          nset)))
                 winners (into {} (mapcat winf groups))]
             (distinct (map (comp winners :affiliation/name)
                            affiliations)))))))))

(defn author-affiliations-from-articles
  [articles author-lookup]
  (let [mf (fn [article]
             (let [{:keys [:article/persons :article/published]} article
                   author (some #(when (= author-lookup
                                          (:author/lookup %))
                                   %)
                                persons)]
               (when author
                 (assoc author :article/published published))))
        authors (keep mf articles)
        af (fn [author]
             (map #(assoc % :article/author author)
                  (:author/affiliations author)))
        affiliations (mapcat af authors)]
    (author-affiliations affiliations)))

;;;; Public API

#?(:clj
   (defn strip-accents
     [s]
     (when-not (str/blank? s)
       (let [s (java.text.Normalizer/normalize
                s java.text.Normalizer$Form/NFD)]
         (-> s
             (str/replace #"[\p{InCombiningDiacriticalMarks}]" "")
             (str/replace #"ø" "o")
             (str/replace #"ł" "l")
             (str/replace #"æ" "ae"))))))

(defn author-label
  [author]
  (let [{:strs [given-names surnames]} author
        snames (concat given-names surnames)
        {:keys [:author/name :author/lookup
                :author/affiliations]} author
        {:keys [:author/given-names :author/surnames]} name
        names (if (seq snames)
                snames
                (concat given-names surnames))]
    (str/join \space names)))

(def two-letter-names
  ["al" "bo" "ed" "ke" "ki" "ky" "om" "oz" "ra" "ry" "si" "sy" "ty" "ya" "aj" "cj" "dj" "jc" "jd" "jj" "jr" "jt" "lj" "rj" "tj" "an" "bo"
   "cy" "di" "el" "fi" "io" "jo" "li" "lo" "lu" "mo" "su" "ty" "vi" "vy" "yi"])

(defn lookup-group
  [s]
  (or
   (when-not (str/blank? s)
     (when-let [i (str/index-of s \space)]
       (when (< i (dec (count s)))
         (subs s 0 (+ 2 i)))))
   s))

#?(:clj
   (defn author-escape
     [s]
     (when-not (str/blank? s)
       (strip-accents
        (str/lower-case (str/trim s))))))

#?(:clj
   (defn name-analysis
     [s reverse?]
     (or
      (when-not (str/blank? s)
        (let [split-name (str/split s #" ")
              split-name (if reverse?
                           (flatten [(rest split-name) (first split-name)])
                           split-name)
              s (if reverse?
                  (str/join " " split-name)
                  s)
              exact-query (author-escape s)
              group-query (author-escape
                           (lookup-group s))
              long-name? (< 2 (count split-name))
              short-name-initials? (if (or long-name? (= (count split-name) 1))
                                     false
                                     (and (not (some #(= (second split-name) %) two-letter-names)) (< (count (second split-name)) 3)))
              result (cond
                       long-name?
                       (let [base-name (author-escape (str (first split-name) " " (second split-name)))
                             other-names (map #(author-escape %) (drop 2 split-name))]
                         {:long-name? true
                          :initials? false
                          :base-name base-name
                          :other-names other-names})
                       short-name-initials?
                       (let [first-name (second split-name)]
                         {:long-name? false
                          :initials? true
                          :first-initial (str (first first-name))
                          :second-initial (if
                                           (= (count first-name) 2)
                                            (str (last first-name))
                                            "")})
                       :else
                       {:long-name? false :initials? false})]
          (assoc result :empty? false :exact-query exact-query :group-query group-query)))
      {:long-name? false :initials? false :empty? true})))

(comment
  (name-analysis "snow l h a" true)
  )
